import { useMemo } from 'react'
import filter from 'lodash/filter'
import type { WorkOrder } from '@ztechapps/common.contract'
import { useConfirmDialog, useError } from '@ztechapps/common.hook'
import { WorkOrderStatusEnum } from '@ztechapps/common.contract'
import { WorkOrderStatus, useWorkOrderStateTransition } from '@ztechapps/work-order.common'

type OnUpdate = (value: WorkOrderStatusEnum) => Promise<void>

// Destructed is a terminal state reached only through the dedicated Destruct action, never
// through this status-menu dropdown.
// Precedent: `apps/panelpunches/admin/src/view/sales/item/hooks/useUpdateOrderStatus.ts:103-106`.
const excludedWorkOrderStatuses = [WorkOrderStatusEnum.Destructed]

export const useUpdateWorkOrderStatus = (workOrder: WorkOrder) => {
  const status = workOrder?.status
  const workOrderId = workOrder?._id
  const [{ changeStatus, moveToCancel, moveToComplete }, { loading, error }] = useWorkOrderStateTransition()

  useError(error)

  const onCancel = () => {
    return moveToCancel(workOrderId)
  }

  const { onConfirm: onCancelWithConfirmation } = useConfirmDialog({
    text: `This will cancel the production and do the following!
    
     1. Production will be locked for editing.
     2. Inventory will be reverted (if the current status is Complete)
     `,
    title: 'Cancel production?',
    buttons: { confirm: { text: 'Yes, cancel it!' } },
    confirmed: onCancel,
  })

  const onComplete = () => {
    return moveToComplete(workOrderId)
  }

  const { onConfirm: onCompleteWithConfirmation } = useConfirmDialog({
    text: `This will complete the production and do the following!
    
     1. Consume the materials (if any) and update the inventory.
     2. Production will be locked for editing.
     `,
    title: 'Complete production?',
    buttons: { confirm: { text: 'Yes, complete it!' } },
    confirmed: onComplete,
  })

  const onUpdate: OnUpdate = async (value) => {
    // If there is no change, do nothing
    if (value === status) {
      return
    }

    // Branch here for the selected status
    switch (value) {
      case WorkOrderStatusEnum.Cancelled:
        await onCancelWithConfirmation()
        break
      case WorkOrderStatusEnum.Complete:
        await onCompleteWithConfirmation()
        break
      default:
        await changeStatus(value, workOrderId)
        break
    }
  }

  const statusMenu = useMemo<{ value: string }[]>(() => {
    return filter(WorkOrderStatus, (item) => !excludedWorkOrderStatuses.includes(item.value))
  }, [])

  return { onUpdate, statusMenu, loading, error }
}
